import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listProjects,
  createProject,
  deleteProject,
  getMe,
  ApiError,
  type Project,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

export function ProjectsPage() {
  const qc = useQueryClient();
  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: listProjects,
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const isAdmin = me?.role === "admin";
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Project | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      setConfirmDelete(null);
      setDeleteError(null);
    },
    onError: (e) =>
      setDeleteError(e instanceof ApiError ? e.message : "Could not delete project"),
  });

  return (
    <div>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="mt-2 text-text-secondary">Each project has its own board.</p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-plus mr-1.5 text-xs" />
          New project
        </button>
      </header>

      {isLoading ? (
        <div className="py-16 text-center text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-xl" />
        </div>
      ) : projects && projects.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => {
            const pct = p.ticketCount ? Math.round((p.doneCount / p.ticketCount) * 100) : 0;
            return (
              <Link
                key={p.id}
                to={`/board/${p.id}`}
                className="group relative rounded-token-lg border border-border bg-bg-card p-5 shadow-token-sm transition-all hover:border-accent hover:shadow-token-md"
              >
                <div className="flex items-center gap-2">
                  <span className="rounded-token-sm bg-accent-muted px-2 py-0.5 font-mono text-xs font-semibold text-accent">
                    {p.key}
                  </span>
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setDeleteError(null);
                        setConfirmDelete(p);
                      }}
                      title="Delete project"
                      className="ml-auto grid h-7 w-7 place-items-center rounded-md text-text-muted opacity-0 transition-all hover:bg-danger/10 hover:text-danger group-hover:opacity-100"
                    >
                      <i className="fa-solid fa-trash text-xs" />
                    </button>
                  )}
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold text-text-heading">
                  {p.name}
                </h3>
                {p.description && (
                  <p className="mt-1 line-clamp-2 text-sm text-text-secondary">{p.description}</p>
                )}

                {/* ticket progress */}
                <div className="mt-4 border-t border-border-light pt-3">
                  {p.ticketCount > 0 ? (
                    <>
                      <div className="flex items-center justify-between text-xs text-text-muted">
                        <span>
                          {p.doneCount}/{p.ticketCount} done
                        </span>
                        <span>{pct}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-bg-tertiary">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            background: "var(--color-accent)",
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <span className="text-xs text-text-muted">No tickets yet</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="rounded-token-lg border border-dashed border-border py-20 text-center">
          <p className="text-text-secondary">No projects yet.</p>
          <button
            onClick={() => setAddOpen(true)}
            className="mt-3 text-sm font-medium text-accent hover:underline"
          >
            Create your first project
          </button>
        </div>
      )}

      <NewProjectModal open={addOpen} onClose={() => setAddOpen(false)} />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete project"
        footer={
          <>
            <button
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
              disabled={remove.isPending}
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ background: "var(--color-danger)" }}
              disabled={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
            >
              {remove.isPending ? "Deleting…" : "Delete project"}
            </button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          Delete{" "}
          <strong className="text-text-heading">
            {confirmDelete?.key} — {confirmDelete?.name}
          </strong>
          {confirmDelete && confirmDelete.ticketCount > 0 && (
            <>
              {" "}
              and its <strong className="text-text-heading">{confirmDelete.ticketCount}</strong>{" "}
              ticket{confirmDelete.ticketCount === 1 ? "" : "s"} (including attachments)
            </>
          )}
          ? This cannot be undone.
        </p>
        {deleteError && (
          <p className="mt-3 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">
            {deleteError}
          </p>
        )}
      </Modal>
    </div>
  );
}

function NewProjectModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createProject({ key, name, description: description || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      setName("");
      setKey("");
      setDescription("");
      setError(null);
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not create project"),
  });

  const inputCls =
    "mb-3 w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open={open} onClose={onClose} title="New project">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="mb-1 block text-xs font-medium text-text-muted">Name</label>
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required />

        <label className="mb-1 block text-xs font-medium text-text-muted">
          Key (2–6 letters, e.g. VP — used in ticket IDs like VP-12)
        </label>
        <input
          className={`${inputCls} font-mono uppercase`}
          value={key}
          onChange={(e) => setKey(e.target.value.toUpperCase())}
          minLength={2}
          maxLength={6}
          required
        />

        <label className="mb-1 block text-xs font-medium text-text-muted">Description (optional)</label>
        <textarea
          className={inputCls}
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {error && (
          <p className="mb-3 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
